import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, GitBranch, Calendar, User, ArrowRight, Link2, AlertTriangle } from 'lucide-react';
import { api } from '../api/client';
import EntityPicker, { ENTITY_ICONS, ENTITY_COLORS, ALL_ENTITY_TYPES } from './EntityPicker';

const DEPENDENCY_KINDS = [
  { value: 'blocks', label: 'Blocks', description: 'Target is blocked until source completes' },
  { value: 'waiting_on', label: 'Waiting On', description: 'Source is waiting for target to complete' },
  { value: 'relates', label: 'Relates To', description: 'General relationship' },
  { value: 'team', label: 'Team Dependency', description: 'Requires another team' },
  { value: 'hardware', label: 'Hardware', description: 'Hardware or equipment dependency' },
  { value: 'asset', label: 'Asset', description: 'Depends on an asset' },
  { value: 'vendor', label: 'Vendor', description: 'External vendor dependency' },
  { value: 'data', label: 'Data', description: 'Data or information dependency' },
  { value: 'system', label: 'System', description: 'System or platform dependency' },
  { value: 'regulatory', label: 'Regulatory', description: 'Compliance or regulatory dependency' }
];

const STATUSES = ['open', 'in_progress', 'closed', 'resolved'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];

/**
 * Dependency Create/Edit Drawer
 */
const DependencyDrawer = ({ 
  isOpen, 
  onClose, 
  dependency = null, 
  defaultSource = null, // { entity_type, entity_id, display }
  onSuccess 
}) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    source: null,
    target: null,
    kind: 'blocks',
    status: 'open',
    severity: 'medium',
    raised_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: '',
    owner: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Load form data when editing
  useEffect(() => {
    if (dependency) {
      setForm({
        name: dependency.name || '',
        description: dependency.description || '',
        source: dependency.source_entity_type ? {
          entity_type: dependency.source_entity_type,
          entity_id: dependency.source_entity_id,
          display: dependency.source_display || ''
        } : null,
        target: dependency.target_entity_type ? {
          entity_type: dependency.target_entity_type,
          entity_id: dependency.target_entity_id,
          display: dependency.target_display || ''
        } : null,
        kind: dependency.kind || 'blocks',
        status: dependency.status?.toLowerCase() || 'open',
        severity: dependency.severity?.toLowerCase() || 'medium',
        raised_date: dependency.raised_date?.split('T')[0] || '',
        due_date: dependency.due_date?.split('T')[0] || '',
        notes: dependency.notes || '',
        owner: dependency.owner || ''
      });
    } else {
      setForm({
        name: '',
        description: '',
        source: defaultSource || null,
        target: null,
        kind: 'blocks',
        status: 'open',
        severity: 'medium',
        raised_date: new Date().toISOString().split('T')[0],
        due_date: '',
        notes: '',
        owner: ''
      });
    }
  }, [dependency, defaultSource]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.source) {
      setError('Source entity is required');
      return;
    }
    if (!form.target) {
      setError('Target entity is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: form.name,
        description: form.description,
        source_entity_type: form.source.entity_type,
        source_entity_id: form.source.entity_id,
        target_entity_type: form.target.entity_type,
        target_entity_id: form.target.entity_id,
        kind: form.kind,
        status: form.status,
        severity: form.severity,
        raised_date: form.raised_date,
        due_date: form.due_date || null,
        notes: form.notes,
        owner: form.owner
      };

      if (dependency?.id) {
        await api.updateDependency(dependency.id, payload);
      } else {
        await api.createDependency(payload);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!dependency?.id) return;
    if (!window.confirm('Are you sure you want to delete this dependency?')) return;

    setDeleting(true);
    try {
      await api.deleteDependency(dependency.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  const title = dependency?.id ? 'Edit Dependency' : 'New Dependency';
  const selectedKind = DEPENDENCY_KINDS.find(k => k.value === form.kind);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      
      {/* Drawer */}
      <div className="relative w-full max-w-xl bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-[var(--clr-border-light)] flex items-center justify-between bg-violet-50">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6 text-violet-600" />
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">{title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Source Entity */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              Source Entity <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-[var(--clr-text-muted)] mb-2">The entity that depends on or blocks another</p>
            <EntityPicker
              multi={false}
              value={form.source ? [form.source] : []}
              onChange={(val) => setForm({ ...form, source: val[0] || null })}
              placeholder="Select source entity..."
            />
          </div>

          {/* Direction Indicator */}
          <div className="flex items-center justify-center gap-4 py-4">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${form.source ? ENTITY_COLORS[form.source.entity_type]?.split(' ').slice(0,2).join(' ') || 'bg-gray-100' : 'bg-gray-100'}`}>
              {form.source ? (
                <>
                  {React.createElement(ENTITY_ICONS[form.source.entity_type] || GitBranch, { className: 'w-4 h-4' })}
                  <span className="text-sm font-medium truncate max-w-32">{form.source.display}</span>
                </>
              ) : (
                <span className="text-sm text-[var(--clr-text-muted)]">Source</span>
              )}
            </div>
            
            <div className="flex flex-col items-center">
              <ArrowRight className="w-6 h-6 text-[var(--clr-text-muted)]" />
              <span className="text-xs text-[var(--clr-text-muted)] capitalize">{form.kind.replace('_', ' ')}</span>
            </div>
            
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${form.target ? ENTITY_COLORS[form.target.entity_type]?.split(' ').slice(0,2).join(' ') || 'bg-gray-100' : 'bg-gray-100'}`}>
              {form.target ? (
                <>
                  {React.createElement(ENTITY_ICONS[form.target.entity_type] || GitBranch, { className: 'w-4 h-4' })}
                  <span className="text-sm font-medium truncate max-w-32">{form.target.display}</span>
                </>
              ) : (
                <span className="text-sm text-[var(--clr-text-muted)]">Target</span>
              )}
            </div>
          </div>

          {/* Target Entity */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              Target Entity <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-[var(--clr-text-muted)] mb-2">The entity that is depended upon or blocked</p>
            <EntityPicker
              multi={false}
              value={form.target ? [form.target] : []}
              onChange={(val) => setForm({ ...form, target: val[0] || null })}
              placeholder="Select target entity..."
            />
          </div>

          {/* Name (optional - auto-generated if empty) */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              placeholder="Auto-generated if empty"
            />
          </div>

          {/* Kind */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Dependency Type</label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
            >
              {DEPENDENCY_KINDS.map(k => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
            {selectedKind && (
              <p className="text-xs text-[var(--clr-text-muted)] mt-1">{selectedKind.description}</p>
            )}
          </div>

          {/* Status & Severity Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {STATUSES.map(s => (
                  <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Severity</label>
              <select
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {SEVERITIES.map(s => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Raised Date
              </label>
              <input
                type="date"
                value={form.raised_date}
                onChange={(e) => setForm({ ...form, raised_date: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              />
            </div>
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Owner
            </label>
            <input
              type="text"
              value={form.owner}
              onChange={(e) => setForm({ ...form, owner: e.target.value })}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              placeholder="Who owns resolving this?"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              placeholder="Describe the dependency..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              placeholder="Additional notes..."
            />
          </div>

          {/* Severity Warning */}
          {form.severity === 'critical' && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Critical Dependency</p>
                <p className="text-xs text-red-600">This dependency has critical severity and may impact project delivery.</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-[var(--clr-border-light)]">
            {dependency?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <div className={`flex gap-2 ${!dependency?.id ? 'ml-auto' : ''}`}>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                data-testid="save-dependency-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DependencyDrawer;
